import PropTypes from 'prop-types';
import { date } from '@eti/utils';
import { useDateFormat, useSearch } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { SegmentNew } from '@eti/types';
import { boundIncludesTrainSegments, getBoundDate, getBoundAsSegment } from '../utils/format';
import { getStopoverType } from '../../../utils/format';
import { isDifferentDestination, isDifferentOrigin } from '../utils/originDestinationUtil';
import TripDetailsBoundOvernightInfo from './TripDetailsBoundOvernightInfo';
import TripDetailsSegment from './TripDetailsSegment';
import TripDetailsBoundHeader from './TripDetailsBoundHeader';
import TripDetailsEventSegment from './TripDetailsEventSegment';

const getItemsToDisplay = (isExpanded, segments = [], boundSegment) => {
  if (isExpanded) {
    return segments;
  }
  if (boundSegment) {
    return [boundSegment];
  }
  return [];
};

const segmentGroupStyle = css`
  background-color: inherit;
`;

const boundSectionStyle = css`
  background-color: inherit;
`;

const TripDetailsBound = ({
  boundNumber,
  flightId,
  includedCheckedBaggage,
  isExpanded,
  isMultiStop,
  segments,
  shouldShowBoundHeader,
  type,
}) => {
  const boundSegment = getBoundAsSegment(segments, boundNumber, includedCheckedBaggage);
  const boundDurationInDays = boundSegment
    ? date.durationInDays(new Date(boundSegment.departuredAt), new Date(boundSegment.arrivedAt))
    : null;
  const items = getItemsToDisplay(isExpanded, segments, boundSegment);
  const { weekday } = useDateFormat();
  const showHeader = shouldShowBoundHeader || isExpanded;
  const includesTrainSegments = boundIncludesTrainSegments(segments);
  const { currentSearchData: search } = useSearch();

  const hasDifferentOrigin = isDifferentOrigin(search?.routes, segments, boundNumber - 1);
  const hasDifferentDestination = isDifferentDestination(search?.routes, segments, boundNumber - 1);

  const stopoverType = getStopoverType(segments);

  return (
    <section className={cx('tripDetailsBound', boundSectionStyle)} data-testid="tripDetails-bound">
      {showHeader && (
        <TripDetailsBoundHeader
          boundNumber={boundNumber}
          departureAt={getBoundDate(segments, weekday)}
          includesTrainSegments={includesTrainSegments}
          isMultiStop={isMultiStop}
          type={type}
        />
      )}
      {items.map((item, segmentIndex) => {
        const isFirstSegment = segmentIndex === 0;
        const isLastSegment = segmentIndex === items.length - 1;

        return (
          <div className={segmentGroupStyle} key={`${flightId}-${item.segmentId}`}>
            {item.__typename === 'TripSegment' ? (
              <TripDetailsSegment
                {...item}
                includesTrainSegments={includesTrainSegments}
                isDifferentDestination={hasDifferentDestination && isLastSegment}
                isDifferentOrigin={hasDifferentOrigin && isFirstSegment}
                isExpanded={isExpanded}
                segmentIncludedCheckedBaggage={item.includedCheckedBaggage}
                stopoverType={stopoverType}
              />
            ) : (
              <TripDetailsEventSegment {...item} isExpanded={isExpanded} />
            )}
          </div>
        );
      })}
      {isExpanded && boundDurationInDays > 0 && (
        <TripDetailsBoundOvernightInfo
          arrivalDate={boundSegment.arrivedAt}
          durationInDays={boundDurationInDays}
          format={weekday}
        />
      )}
    </section>
  );
};

TripDetailsBound.propTypes = {
  boundNumber: PropTypes.number,
  flightId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.number,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  isExpanded: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  segments: PropTypes.arrayOf(SegmentNew),
  shouldShowBoundHeader: PropTypes.bool,
  type: PropTypes.string,
};

export default TripDetailsBound;
